import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  motion,
  useMotionValue,
  useTransform,
  useSpring,
  AnimatePresence,
} from "motion/react";
import Typewriter from "typewriter-effect";
import useRipple from "../../hooks/useRipple.js";
import "../../hooks/useRipple.css";

import { useSuperAdminLoginMutation } from "../../store/auth/auth.api";
import { useSchoolAdminLoginMutation } from "../../store/auth/school.auth.api";
import { getIsAuthenticated } from "../../store/auth/selectors";
import { USER_TYPES } from "../../store/auth/auth.slice";
import genis_logo from "../../assets/images/genius_logo.svg";
import login_Bg from "../../assets/images/login-bg.png";
import footer_Bg from "../../assets/images/footer-bg.png";
import hide_password_icon from "../../assets/images/hide_password_icon.png";
import show_password_icon from "../../assets/images/show_password_icon.png";

import "./Login.css";

const LoginPage = () => {
  const [loginError, setLoginError] = useState("");
  const [accountType, setAccountType] = useState("Infinity Admin");
  const [phone, setPhone] = useState("");
  const [isdCode, setIsdCode] = useState("+91");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const pageRef = useRef(null);
  const loginButtonRef = useRef(null);
  useRipple(loginButtonRef);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [superAdminLogin, { isLoading: isSuperLoading, error: superError }] =
    useSuperAdminLoginMutation();
  const [schoolAdminLogin, { isLoading: isSchoolLoading, error: schoolError }] =
    useSchoolAdminLoginMutation();

  const isAuthenticated = useSelector(getIsAuthenticated);

  // Watch for authentication state changes after login attempt
  const [loginAttempted, setLoginAttempted] = useState(false);

  // Motion values for mouse tracking
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  // Spring animations for smooth mouse interactions - much more subtle
  const springConfig = { stiffness: 50, damping: 30, mass: 0.5 };
  const mouseXSpring = useSpring(mouseX, springConfig);
  const mouseYSpring = useSpring(mouseY, springConfig);

  // Transform values for very subtle parallax effects
  const backgroundLayer1X = useTransform(mouseXSpring, [-0.5, 0.5], [-8, 8]);
  const backgroundLayer1Y = useTransform(mouseYSpring, [-0.5, 0.5], [-5, 5]);
  const backgroundLayer2X = useTransform(mouseXSpring, [-0.5, 0.5], [-4, 4]);
  const backgroundLayer2Y = useTransform(mouseYSpring, [-0.5, 0.5], [-3, 3]);

  // Footer wave animation transforms - very subtle
  const footerX = useTransform(mouseXSpring, [-0.5, 0.5], [-6, 6]);
  const footerY = useTransform(mouseYSpring, [-0.5, 0.5], [-3, 3]);

  useEffect(() => {
    if (loginAttempted) {
      if (isAuthenticated) {
        navigate("/planner");
      } else {
        setLoginError(
          "Access denied. Your role does not have permission to access this application."
        );
      }
      setLoginAttempted(false);
    }
  }, [isAuthenticated, loginAttempted, navigate]);

  useEffect(() => {
    const handleMouseMove = (event) => {
      // Skip mouse parallax on mobile/tablet
      if (window.innerWidth < 768) return;

      if (pageRef.current) {
        const rect = pageRef.current.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        const x = (event.clientX - centerX) / (rect.width / 2);
        const y = (event.clientY - centerY) / (rect.height / 2);

        // Update motion values
        mouseX.set(Math.max(-0.5, Math.min(0.5, x)));
        mouseY.set(Math.max(-0.5, Math.min(0.5, y)));
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [mouseX, mouseY]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      const loginType =
        accountType === "School Admin"
          ? USER_TYPES.SCHOOL
          : USER_TYPES.SUPERADMIN;

      if (loginType === USER_TYPES.SCHOOL) {
        const body = {
          isd_code: isdCode,
          phone,
          password,
        };
        const response = await schoolAdminLogin({ body }).unwrap();
        if (response?.detail) {
          throw new Error(response.detail);
        }
      } else {
        const body = {
          phone,
          password,
          isd_code: isdCode,
          login_type: loginType,
        };
        await superAdminLogin({ body }).unwrap();
      }
      setLoginAttempted(true);
    } catch (err) {
      console.error("Login failed:", err);
      setLoginError(
        "Login failed. Please check your credentials and try again."
      );
    }
  };

  return (
    <div className="login-page" ref={pageRef}>
      {/* Multi-layer parallax background - Layer 1 (deeper) */}
      <motion.div
        style={{
          position: "absolute",
          inset: 0,
          height: "70%",
          backgroundImage: `url(${login_Bg})`,
          backgroundRepeat: "repeat",
          backgroundSize: "auto",
          zIndex: -3,
          opacity: 0.4,
          x: backgroundLayer1X,
          y: backgroundLayer1Y,
        }}
        animate={{
          backgroundPosition: [
            "0% 50%",
            "30% 20%",
            "-25% 80%",
            "15% 35%",
            "0% 50%",
          ],
        }}
        transition={{
          duration: 28,
          ease: "easeInOut",
          repeat: Infinity,
        }}
      />

      {/* Multi-layer parallax background - Layer 2 (closer) */}
      <motion.div
        style={{
          position: "absolute",
          inset: 0,
          height: "70%",
          backgroundImage: `url(${login_Bg})`,
          backgroundRepeat: "repeat",
          backgroundSize: "110%",
          zIndex: -2,
          opacity: 0.9,
          x: backgroundLayer2X,
          y: backgroundLayer2Y,
        }}
        animate={{
          backgroundPosition: [
            "100% 50%",
            "70% 75%",
            "90% 25%",
            "80% 60%",
            "100% 50%",
          ],
          scale: [1, 1.02, 0.98, 1.01, 1],
        }}
        transition={{
          duration: 22,
          ease: "easeInOut",
          repeat: Infinity,
        }}
      />

      {/* Footer background with very subtle wave simulation */}
      <motion.div
        style={{
          position: "absolute",
          bottom: 0,
          left: "50%",
          width: "105%",
          height: "45%",
          backgroundImage: `url(${footer_Bg})`,
          backgroundSize: "cover",
          backgroundPosition: "center bottom",
          backgroundRepeat: "no-repeat",
          zIndex: -1,
          translateX: "-50%",
          x: footerX,
          y: footerY,
        }}
        animate={{
          y: [0, -2, 1, -1, 0],
          x: [0, 2, -1, 1, 0],
        }}
        transition={{
          duration: 25,
          ease: "easeInOut",
          repeat: Infinity,
        }}
      />

      <div className="login-container">
        <div className="login-card">
          <div className="left-panel">
            <div className="logo">
              <motion.img
                src={genis_logo}
                alt="Infinity Learn Logo"
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{
                  delay: 1,
                  duration: 0.4,
                  scale: { type: "spring", visualDuration: 0.4, bounce: 0.5 },
                }}
              />
            </div>
            <h2>
              <Typewriter
                options={{
                  strings: ["Welcome Back to <br/>Infinity Learn"],
                  autoStart: true,
                  loop: true,
                }}
              />
            </h2>
            <div className="yellow-line"></div>
          </div>

          <div className="right-panel">
            <h1>LOGIN</h1>
            <form>
              <div className="form-group">
                <label>Account Type</label>
                <select
                  value={accountType}
                  onChange={(e) => setAccountType(e.target.value)}
                >
                  <option value="Infinity Admin">Infinity Admin</option>
                  <option value="School Admin">School Admin</option>
                </select>
              </div>

              <div className="form-group">
                <label>ISD Code</label>
                <select
                  value={isdCode}
                  onChange={(e) => setIsdCode(e.target.value)}
                >
                  <option value="+91">+91 India</option>
                  <option value="+971">+971 UAE</option>
                </select>
              </div>

              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="text"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Password</label>
                <div className="password-input-container">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <AnimatePresence mode="wait" initial={false}>
                    <motion.img
                      key={showPassword ? "hide-icon" : "show-icon"}
                      src={
                        showPassword ? hide_password_icon : show_password_icon
                      }
                      alt="toggle password visibility"
                      className="password-toggle-icon"
                      onClick={() => setShowPassword((prev) => !prev)}
                      initial={{ opacity: 0, rotate: -140, scale: 0.8 }}
                      animate={{ opacity: 1, rotate: 0, scale: 1 }}
                      exit={{ opacity: 0, rotate: 140, scale: 0.8 }}
                      transition={{
                        type: "spring",
                        stiffness: 360,
                        damping: 16,
                        mass: 0.35,
                      }}
                    />
                  </AnimatePresence>
                </div>
              </div>

              <button
                type="button"
                className="login-button"
                disabled={isSuperLoading || isSchoolLoading}
                ref={loginButtonRef}
                style={{ position: "relative", overflow: "hidden" }}
                onClick={handleSubmit}
              >
                {isSuperLoading || isSchoolLoading ? "Logging in..." : "LOGIN"}
              </button>
              {(superError || schoolError || loginError) && (
                <p className="login-error-message">
                  {loginError || "Login failed. Please refresh and try again."}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
